import {IRouteDefinition, IRouteParams, TRouteId} from "../index";
import {getRouteDefinition, getRouteDefinitions} from "../constants/routeLookup";
import {getHash} from "../constants/routeHash";
import {_} from "lodash";
import {hasher} from "hasher";

const PAGE_SHELL_COMPONENT = 'component/page-shell/details';

/**
 * Longest-first family prefixes stripped from tab ids for short URLs
 * (rulesetinfo → info, messageflowdetailsinfo → info).
 */
const TAB_ID_FAMILY_PREFIXES = [
    'messageflowdetails',
    'manualapiuser',
    'manualdepartment',
    'manualgroup',
    'manualuser',
    'systemaccess',
    'synctemplate',
    'importhistory',
    'syncfields',
    'apiuser',
    'formdata',
    'department',
    'customer',
    'settings',
    'ruleset',
    'group',
    'user',
    'about_',
    'sync',
    'log',
] as const;

/**
 * Explicit short hash segments when family-strip is wrong or insufficient
 * (systemaccessroles → permissions, not roles).
 */
const TAB_HASH_SEGMENT_BY_ID: Partial<Record<TRouteId, string>> = {
    systemaccessroles: 'permissions',
    // Avoid both sync search tabs collapsing to "search"; match nested sync/* paths.
    syncstatusv2: 'status',
    importhistorysearch: 'importhistory',
    // Avoid …/sync/importhistory/{id}/sync; match label "synchronization cycle".
    importhistorysync: 'synccycle',
    synctemplatesearch: 'template',
};

/**
 * Path segments that participate in legacy path-prefix stripping / diagnostics
 * (o/{orgnr}/user/{id} → user).
 */
const SHORT_TAB_PATH_PREFIXES = new Set<string>([
    'user',
    'group',
    'department',
    'apiuser',
    'manualuser',
    'manualgroup',
    'manualdepartment',
    'manualapiuser',
    'formdata',
    'systemaccess',
    'ruleset',
    'synctemplate',
    'importhistory',
    'messageflow',
    'sync',
    'log',
]);

/** Cached expand result — route defs are static for the app lifetime. */
let cachedPageShellTabRoutes: IPageShellTabRoute[] | null = null;

/** Strip optional crossroads segments like /:options: from a hash template. */
export function stripOptionalHashSegments(hash: string): string {
    return hash.replace(/\/:.*:/g, '');
}

/** First hash pattern on a route definition. */
export function getPrimaryRouteHash(routeDef: IRouteDefinition): string | null {
    if (!routeDef?.hash) {
        return null;
    }
    return typeof routeDef.hash === 'string' ? routeDef.hash : (routeDef.hash[0] ?? null);
}

/** Resolve tab route ids configured on a PageShell (string entries or defs). */
export function resolveShellTabIds(shellDef: IRouteDefinition): TRouteId[] {
    if (!shellDef?.tabs?.length) {
        return [];
    }
    return shellDef.tabs.map((tabEntry) => {
        if (typeof tabEntry === 'string') {
            return tabEntry as TRouteId;
        }
        return getRouteDefinition(tabEntry)?.id
            ?? (tabEntry as IRouteDefinition | undefined)?.id
            ?? null;
    }).filter((id): id is TRouteId => id != null);
}

/**
 * Object-type path segment for a PageShell (o/{orgnr}/user/{id} → user).
 */
export function getPageShellTabPathPrefix(shellDef: IRouteDefinition): string | null {
    const hash = getPrimaryRouteHash(shellDef);
    if (!hash) {
        return null;
    }
    const base = stripOptionalHashSegments(hash);
    const match = base.match(/^o\/\{orgnr\}\/([^/]+)\//);
    const prefix = match?.[1] ?? null;
    return prefix && SHORT_TAB_PATH_PREFIXES.has(prefix) ? prefix : null;
}

/** Strip the longest matching family prefix from a tab id. */
function stripTabIdFamilyPrefix(tabId: string): string {
    for (const prefix of TAB_ID_FAMILY_PREFIXES) {
        if (tabId.startsWith(prefix) && tabId.length > prefix.length) {
            return tabId.slice(prefix.length);
        }
    }
    return tabId;
}

/** Candidate short segment before shell collision checks. */
function candidateTabHashSegment(tabId: TRouteId): string {
    return TAB_HASH_SEGMENT_BY_ID[tabId] ?? stripTabIdFamilyPrefix(tabId);
}

/**
 * Hash path segment for a tab under a PageShell.
 * Uses explicit overrides, then family-prefix strip; keeps the full tab id when
 * another tab in the same shell would collide on the short segment.
 */
export function getPageShellTabHashSegment(shellDef: IRouteDefinition, tabId: TRouteId): string {
    const candidate = candidateTabHashSegment(tabId);
    if (candidate === tabId) {
        return tabId;
    }
    const tabIds = resolveShellTabIds(shellDef);
    const collision = tabIds.some((other) =>
        other !== tabId && candidateTabHashSegment(other) === candidate,
    );
    return collision ? tabId : candidate;
}

/**
 * Non-tab child route mounted inside a PageShell (e.g. under userdetails):
 * declares `pageShell` and its own component, but is not one of the shell's configured tabs.
 * Its component fills the shell body while its `tabDefault` tab stays highlighted.
 */
export function isShellContentChildRoute(
    shellDef: IRouteDefinition | null | undefined,
    routeDef: IRouteDefinition | null | undefined,
): boolean {
    if (!shellDef || !routeDef?.pageShell || routeDef.pageShell !== shellDef.id) {
        return false;
    }
    if (!routeDef.component?.name) {
        return false;
    }
    return !resolveShellTabIds(shellDef).includes(routeDef.id);
}

/** Resolve a short URL segment back to a shell tab id (permissions → systemaccessroles). */
export function getTabIdFromShortHashSegment(
    shellDef: IRouteDefinition,
    segment: string,
): TRouteId | null {
    const tabIds = resolveShellTabIds(shellDef);
    if (tabIds.includes(segment as TRouteId)) {
        return segment as TRouteId;
    }
    for (const tabId of tabIds) {
        if (getPageShellTabHashSegment(shellDef, tabId) === segment) {
            return tabId;
        }
    }
    // Pre-override family strip (e.g. sync → importhistorysync after rename to synccycle).
    for (const tabId of tabIds) {
        const stripped = stripTabIdFamilyPrefix(tabId);
        if (stripped !== tabId && stripped === segment) {
            return tabId;
        }
    }
    return null;
}

export interface IPageShellTabRoute {
    hash: string | RegExp;
    routeDef: IRouteDefinition;
}

/** Clear memoized tab routes (tests). */
export function clearPageShellTabRoutesCache(): void {
    cachedPageShellTabRoutes = null;
}

/** True when a hash template already lives under this shell's path. */
function isHashUnderShellBase(hash: string, shellBase: string): boolean {
    return hash === shellBase || hash.startsWith(`${shellBase}/`);
}

/** Nested tab hashes under a PageShell parent (short segment + optional legacy aliases). */
function nestedHashesForShellTab(shellDef: IRouteDefinition, shellBase: string, tabId: TRouteId): string[] {
    const shortSegment = getPageShellTabHashSegment(shellDef, tabId);
    const hashes = [`${shellBase}/${shortSegment}`];
    // Previous auto-short before TAB_HASH_SEGMENT_BY_ID override (e.g. sync → synccycle).
    const familyStripped = stripTabIdFamilyPrefix(tabId);
    if (familyStripped !== tabId && familyStripped !== shortSegment) {
        hashes.push(`${shellBase}/${familyStripped}`);
    }
    // Legacy bookmarks used the full tab id as the path segment.
    if (shortSegment !== tabId) {
        hashes.push(`${shellBase}/${tabId}`);
    }
    return hashes;
}

/**
 * Derive child tab routes for PageShell parents. Tabs with existing hashes get pageShell injected;
 * tabs without hashes get a computed hash under the parent path.
 * Shared tabs whose own hash is outside the shell (e.g. departmenthierarchy on details)
 * also get nested hashes under the shell so each shell can navigate without stealing the other.
 */
export function expandPageShellTabRoutes(): IPageShellTabRoute[] {
    if (cachedPageShellTabRoutes) {
        return cachedPageShellTabRoutes;
    }
    const ret: IPageShellTabRoute[] = [];
    const registered = new Set<string>();

    const register = (hash: string | RegExp, routeDef: IRouteDefinition) => {
        const key = typeof hash === 'string' ? hash : hash.toString();
        if (registered.has(key)) {
            return;
        }
        registered.add(key);
        ret.push({hash, routeDef});
    };

    getRouteDefinitions().forEach((shellDef: IRouteDefinition) => {
        if (shellDef.component?.name !== PAGE_SHELL_COMPONENT || !shellDef.tabs?.length) {
            return;
        }
        const shellHash = getPrimaryRouteHash(shellDef);
        if (!shellHash) {
            return;
        }
        const shellBase = stripOptionalHashSegments(shellHash);

        shellDef.tabs.forEach((tabEntry) => {
            const tabDef = getRouteDefinition(tabEntry);
            if (!tabDef) {
                return;
            }
            const tabHashes: (string | RegExp)[] = [];
            if (tabDef.hash) {
                const ownHashes = typeof tabDef.hash === 'string' ? [tabDef.hash] : [...tabDef.hash];
                const underThisShell = ownHashes.some(
                    (hash) => typeof hash === 'string' && isHashUnderShellBase(hash, shellBase),
                );
                if (underThisShell) {
                    // Tab URL already belongs under this shell (e.g. department/search).
                    tabHashes.push(...ownHashes);
                } else {
                    // Standalone / shared tab: first shell claims the own hashes; every shell
                    // also gets nested paths so details can host the same tab without leaving.
                    const canClaimOwn = ownHashes.some((hash) => !registered.has(hash));
                    if (canClaimOwn) {
                        tabHashes.push(...ownHashes);
                    }
                    tabHashes.push(...nestedHashesForShellTab(shellDef, shellBase, tabDef.id));
                }
            } else {
                tabHashes.push(...nestedHashesForShellTab(shellDef, shellBase, tabDef.id));
            }
            tabHashes.forEach((hash) => {
                // Inherit shell hashPrefixOrgId so expanded tabs match getRouteDefFromHash lookup.
                const tabRouteBase = {
                    pageShell: shellDef.id,
                    hashPrefixOrgId: shellDef.hashPrefixOrgId,
                };
                if (typeof hash !== 'string') {
                    register(hash, _.defaultsDeep(tabRouteBase, tabDef));
                    return;
                }
                if (!hash.startsWith('o/') && shellDef.hashPrefixOrgId !== false) {
                    return;
                }
                register(hash, _.defaultsDeep({
                    ...tabRouteBase,
                    hash,
                }, tabDef));
            });
        });
    });

    // Non-tab shell children: register their own hashes at tab priority
    // so they beat the shell parent's /:options: pattern in Crossroads.
    getRouteDefinitions().forEach((def: IRouteDefinition) => {
        if (!def.pageShell || !def.hash) {
            return;
        }
        const shellDef = getRouteDefinition(def.pageShell);
        if (!shellDef || !isShellContentChildRoute(shellDef, def)) {
            return;
        }
        const ownHashes = typeof def.hash === 'string' ? [def.hash] : [...def.hash];
        ownHashes.forEach((hash) => register(hash, def));
    });

    cachedPageShellTabRoutes = ret;
    return ret;
}

/** Find expanded tab route entry for a shell parent + tab id. */
export function findShellTabRoute(shellId: TRouteId, tabId: TRouteId): IPageShellTabRoute | null {
    return expandPageShellTabRoutes().find(
        (entry) => entry.routeDef.id === tabId && entry.routeDef.pageShell === shellId,
    ) ?? null;
}

/** Resolve navigable hash for a tab under a PageShell (uses expanded child routes). */
export function getShellTabHash(
    shellId: TRouteId,
    tabId: TRouteId,
    routeParams: IRouteParams,
    withHash = true,
): string | null {
    const entry = findShellTabRoute(shellId, tabId);
    if (entry && typeof entry.hash === 'string') {
        return getHash({_extend: tabId, hash: entry.hash}, routeParams, withHash);
    }
    const tabDef = getRouteDefinition(tabId);
    if (tabDef?.hash) {
        return getHash(tabId, routeParams, withHash);
    }
    return null;
}

/** Shell parent id for a tab, from expanded registrations. */
export function findShellParentForTabId(tabId: TRouteId): TRouteId | null {
    const entry = expandPageShellTabRoutes().find((e) => e.routeDef.id === tabId);
    return entry?.routeDef.pageShell ?? getRouteDefinition(tabId)?.pageShell ?? null;
}

/** True when route is a PageShell parent (not a tab child). */
export function isPageShellParentRoute(routeDef: IRouteDefinition): boolean {
    return routeDef?.component?.name === PAGE_SHELL_COMPONENT
        && !!routeDef.tabs?.length
        && !routeDef.pageShell;
}

/** Navigate to a tab under a PageShell parent. User tab clicks always push history. */
export function navigateToShellTab(
    shellId: TRouteId,
    tabId: TRouteId,
    routeParams: IRouteParams,
    replace = false,
): boolean {
    const hash = getShellTabHash(shellId, tabId, routeParams, false);
    if (!hash) {
        return false;
    }
    // Tab bar clicks pass replace=false; only parent-url canonicalization uses replace.
    if (replace) {
        // Defer so an outer hasher.setHash can finish writing location.hash first.
        // hasher fires `changed` before updating the URL; a sync replaceHash inside that
        // handler changes _hash so setHash skips its push (hasher #39), replacing the
        // previous history entry instead (e.g. search → detail after org switch).
        queueMicrotask(() => {
            hasher.replaceHash(hash);
        });
    } else {
        hasher.setHash(hash);
    }
    return true;
}

/** Find PageShell parent id for a tab route id, if registered via pageShell. */
export function findPageShellParentForTab(tabId: TRouteId): TRouteId | null {
    return findShellParentForTabId(tabId);
}
